import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, Grid } from 'styled-css-grid';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from 'mdi-react/CloseIcon';
import PlusCircleOutlineIcon from 'mdi-react/PlusCircleOutlineIcon';

import {
  FormField,
  formSelector,
  xcriticalFormPropertyChange,
} from '@xcritical/forms';

import { Spacing } from '@crm-framework/spacing';
import { IconButton } from '@crm-framework/icon-button';
import { Label } from '@crm-framework/label';
import { InputFormField, SelectFormField } from '@crm-framework/form-fields';
import { ISelectOption } from '@crm-framework/types';
import { registryActions, useRegistry } from '@crm-framework/registry';

import {
  PROMO_FORM_NAME,
  limitTypes,
  PROMO_REGISTRIES,
  limitTypesKeys,
} from '../consts';
import { ILimitationProps, IPromoModel } from '../types';

import { SectionHeader } from './styled';

const LIMIT_TYPE = 'limitType';

export const Limitation: React.FC<ILimitationProps> = ({ isNew, disabled }) => {
  const dispatch = useDispatch();
  const { model } = useSelector((state) =>
    formSelector<IPromoModel>(state, PROMO_FORM_NAME)
  );
  const [formFieldNames, setFormFieldNames] = useState<string[]>([]);
  const isReset = useRegistry<boolean>(PROMO_REGISTRIES.IS_RESET_FORM, false);

  useEffect(() => {
    const initFieldNames: string[] = [];

    if (!isNew) {
      Object.keys(model).forEach((key) => {
        if (limitTypesKeys.includes(key)) initFieldNames.push(key);
      });
    } else initFieldNames.push(selectOptions[0].value);

    setFormFieldNames(initFieldNames);

    if (isReset) {
      dispatch(registryActions.set(PROMO_REGISTRIES.IS_RESET_FORM, false));
    }
  }, [isNew, isReset]);

  const selectOptions = useMemo(
    () =>
      limitTypes.reduce<{ label: string; value: string }[]>((acc, item) => {
        if (!formFieldNames.includes(item.value)) {
          acc.push(item);
        }

        return acc;
      }, []),
    [formFieldNames]
  );

  const onChange = useCallback(
    (selectOption: ISelectOption | null, index: number) => {
      dispatch(
        xcriticalFormPropertyChange(
          PROMO_FORM_NAME,
          String(selectOption?.value),
          model[formFieldNames[index]]
        )
      );
      dispatch(
        xcriticalFormPropertyChange(
          PROMO_FORM_NAME,
          formFieldNames[index],
          undefined
        )
      );

      const updatedNamesList = [...formFieldNames];
      updatedNamesList.splice(index, 1, String(selectOption?.value));
      setFormFieldNames(updatedNamesList);
    },
    [formFieldNames]
  );

  const handleAddRow = useCallback(() => {
    if (formFieldNames.length === limitTypes.length) return;

    setFormFieldNames([...formFieldNames, selectOptions[0].value]);

    dispatch(
      xcriticalFormPropertyChange(PROMO_FORM_NAME, selectOptions[0].value, null)
    );
  }, [formFieldNames, selectOptions]);

  const handleRemoveRow = useCallback(
    (index: number) => {
      dispatch(
        xcriticalFormPropertyChange(
          PROMO_FORM_NAME,
          formFieldNames[index],
          undefined
        )
      );

      const updatedNamesList = [...formFieldNames];
      updatedNamesList.splice(index, 1);
      setFormFieldNames(updatedNamesList);
    },
    [formFieldNames]
  );

  return (
    <Spacing mt={20} mb={10}>
      <Grid columns="46% 46% 8%" justifyContent="space-between">
        <Cell width={3}>
          <SectionHeader>
            <div>Limitation</div>
            <IconButton
              withCircleHover
              icon={PlusCircleOutlineIcon}
              onClick={handleAddRow}
              disabled={formFieldNames.length === limitTypes.length || disabled}
              title="Add Limit"
            />
          </SectionHeader>
        </Cell>

        <Cell>
          <Label htmlFor={LIMIT_TYPE} isRequired>
            Limit Type
          </Label>
        </Cell>
        <Cell>
          <Label htmlFor="value" isRequired>
            Value
          </Label>
        </Cell>
        <Cell />

        {formFieldNames.map((fieldName: string, index: number) => (
          <>
            <Cell>
              <SelectFormField
                id={LIMIT_TYPE}
                shouldFitContainer
                name={LIMIT_TYPE}
                options={selectOptions}
                onChange={(value) => onChange(value, index)}
                value={limitTypes.find((item) => item.value === fieldName)}
                errorProps={{ shouldFitContainer: false }}
                isSearchable
                disabled={disabled}
              />
            </Cell>
            <Cell>
              <FormField
                id="value"
                shouldFitContainer
                name={fieldName}
                type="number"
                component={InputFormField}
                errorProps={{ shouldFitContainer: false }}
                disabled={disabled}
              />
            </Cell>

            <Cell>
              {formFieldNames.length > 1 ? (
                <IconButton
                  withCircleHover
                  icon={CloseIcon}
                  onClick={() => handleRemoveRow(index)}
                  disabled={disabled}
                />
              ) : null}
            </Cell>
          </>
        ))}
      </Grid>
    </Spacing>
  );
};
